/**
 * ProductFruits User Sync Module
 * Manages user data synchronization between shell and iframes
 */

import { serializeError } from './utils';

const CHATLYN_NAMESPACE_KEY = '__chatlyn';

const getHostWindow = () => window;

const getShellPayloadFromWindow = () => {
  const hostWindow = getHostWindow();
  return hostWindow[CHATLYN_NAMESPACE_KEY]?.productFruits?.shell?.payload;
};

const getProductFruitsInstance = () => {
  const hostWindow = getHostWindow();
  return hostWindow.productFruits;
};

/**
 * Manages user data synchronization
 */
export class ProductFruitsUserSync {
  constructor() {
    this.userData = undefined;
  }

  /**
   * Set user data and merge with shell payload
   */
  setUserData(user) {
    if (!user) {
      return;
    }

    this.userData = {
      ...user,
      preferences: user.preferences || {},
    };
  }

  /**
   * Get complete user data merged with shell payload
   */
  getCompleteUserData(overrideProps = {}) {
    const shellPayload = getShellPayloadFromWindow();
    const baseUser = this.userData;

    if (!shellPayload && !baseUser) {
      return undefined;
    }

    const username =
      baseUser?.username ||
      shellPayload?.username ||
      baseUser?.email ||
      shellPayload?.email;

    if (!username) {
      return undefined;
    }

    return {
      ...(baseUser || {}),
      username,
      email: baseUser?.email || shellPayload?.email,
      firstname: baseUser?.firstname || shellPayload?.firstname,
      lastname: baseUser?.lastname || shellPayload?.lastname,
      signUpAt: baseUser?.signUpAt || shellPayload?.signUpAt,
      role: baseUser?.role || shellPayload?.role,
      hmac: shellPayload?.hmac || baseUser?.hmac,
      props: {
        ...(shellPayload?.props || {}),
        ...(baseUser?.props || {}),
        ...overrideProps,
      },
    };
  }

  /**
   * Update user properties and sync to ProductFruits instance
   */
  updateProperties(props) {
    if (!props) {
      return;
    }

    const mergedUserData = this.getCompleteUserData(props);
    if (this.userData && mergedUserData?.props) {
      this.userData = {
        ...this.userData,
        props: mergedUserData.props,
      };
    }

    // Sync to ProductFruits instance
    const productFruitsInstance = getProductFruitsInstance();
    const hostWindow = getHostWindow();

    if (
      productFruitsInstance?.identifyUser &&
      hostWindow.productFruitsIsReady &&
      mergedUserData?.username
    ) {
      try {
        productFruitsInstance.identifyUser({
          username: mergedUserData.username,
          props: mergedUserData.props || props,
        });
      } catch (error) {
        this.emitDebugEvent('identify-user-error', {
          error: serializeError(error),
        });
      }
    }
  }

  /**
   * Get current user data
   */
  getUserData() {
    return this.userData;
  }

  emitDebugEvent(eventName, payload = {}) {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(
      new CustomEvent('productfruits:debug', {
        detail: {
          eventName,
          ...payload,
        },
      })
    );
  }
}

